import { markAuthoredBlankLines } from '@/utils/authored_blank_lines';
import {
  YURTZEITEN_COMMENT_BLOCK_ATTR,
  YURTZEITEN_COMMENT_COPY_ATTR,
  YURTZEITEN_COMMENT_TRACK_ATTR,
  YURTZEITEN_COMMENT_VIEWPORT_ATTR,
  YURTZEITEN_ENTRY_ATTR,
  YURTZEITEN_ENTRY_VALUE,
  YURTZEITEN_PHOTO_FULL_WIDTH_ATTR,
  YURTZEITEN_PHOTO_IMAGE_ATTR,
} from '@/utils/yurtzeiten_design_templates';

export interface YurtzeitenRenderEntry_N {
  englishName: string;
  // The two halves the English name is stored in, so a design can print either
  // on its own line. `englishName` is the two of them joined.
  firstName: string;
  lastName: string;
  hebrewName: string;
  gregorianDate: string;
  hebrewDate: string;
  comment?: string;
  image?: string | null;
  // Legacy/editor aliases. The saved HTML should use the canonical names above,
  // but older custom designs may still carry one of these.
  english?: string;
  hebrew?: string;
  gregorian?: string;
  name?: string;
}

export interface RenderYurtzeitenDesignArgs_N {
  // The design authored in the editor.
  html: string;
  // One resolved entry per block the design carries.
  entries: YurtzeitenRenderEntry_N[];
  // Falls back for the photo placeholder when no entry carries a picture.
  leadImage?: string | null;
  // Production cards do not have editor sample content to pad an incomplete
  // final card. Remove authored entry blocks without a matching real record.
  removeUnfilledEntries?: boolean;
}

// Swap {token} for the entry's value across a subtree's text nodes.
//
// Done on text nodes rather than by string-replacing the HTML so a value can
// never introduce markup, and a token sitting inside an attribute (a URL, say)
// is left alone.
const substituteVariables = (
  root: Element,
  entry: YurtzeitenRenderEntry_N,
  shouldSubstituteNode: (node: Text) => boolean = () => true
): void => {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (
      node.nodeValue &&
      node.nodeValue.indexOf('{') !== -1 &&
      shouldSubstituteNode(node)
    ) {
      targets.push(node);
    }
  }

  targets.forEach((node) => {
    node.nodeValue = (node.nodeValue as string).replace(
      /\{(\w+)\}/g,
      (match, key) =>
        Object.prototype.hasOwnProperty.call(entry, key)
          ? String((entry as any)[key] ?? '')
          : match
    );
  });
};

const COMMENT_TOKEN = '{comment}';

// What the editor can produce as a block. Everything else it emits — <span>,
// <strong>, <em> — is inline, and an inline box cannot be given a width.
const COMMENT_BLOCK_TAGS = new Set([
  'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE',
]);

/**
 * Tag the block each {comment} is written into, before the token is replaced.
 *
 * A card is drawn at its content's width, and a comment is the one field with no
 * natural length to it — left to contribute, a long note alone decides how wide
 * the card is and stretches it out. Marking the block lets the stylesheet keep it
 * out of that measurement, so the note wraps inside the width the rest of the
 * design asked for instead.
 *
 * The token normally sits in a <span> carrying its font, so the box that matters
 * is the block that span is in. Any wrapper holding nothing but that block is
 * marked instead — it is the outermost such box the card would be measured from.
 */
const markCommentBlocks = (root: Element): void => {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (!node.nodeValue || node.nodeValue.indexOf(COMMENT_TOKEN) === -1) continue;

    let block: Element | null = node.parentElement;
    while (block && block !== root && !COMMENT_BLOCK_TAGS.has(block.tagName)) {
      block = block.parentElement;
    }
    if (!block || block === root) continue;

    // Never climb out of a layout container: those are the grid and flex boxes
    // the design's structure is built from, and their width is not the comment's
    // to give up.
    let parent = block.parentElement;
    while (
      parent
      && parent !== root
      && !parent.hasAttribute(YURTZEITEN_ENTRY_ATTR)
      && COMMENT_BLOCK_TAGS.has(parent.tagName)
      && parent.childElementCount === 1
      && !(parent.textContent || '').replace(block.textContent || '', '').trim()
    ) {
      block = parent;
      parent = block.parentElement;
    }

    // Unless the note is the whole design. Nothing else would be left for the
    // card to take a width from, so it has to take it from the note.
    if (root.childElementCount === 1 && root.firstElementChild === block) continue;

    block.setAttribute(YURTZEITEN_COMMENT_BLOCK_ATTR, 'true');
  }
};

/**
 * Put every marked note inside the window it will be scrolled through.
 *
 *   <div data-yz-comment-viewport>   the window; height and clipping are the
 *     <div data-yz-comment-track>    renderer's, measured against the artboard
 *       <div data-yz-comment-copy>   one lap of the loop
 *         <p data-yz-comment>…</p>   the note, exactly as it was authored
 *       <div data-yz-comment-copy>   the same lap again, so the note re-enters
 *         <p data-yz-comment>…</p>   the bottom as the first leaves the top
 *
 * The wrapper is always built and always inert: with no height on the viewport
 * and the trailing copy displayed as nothing, this renders identically to the
 * bare note. Only a card that has measured an overflow turns it on — see
 * `applyCommentScrollers` in Yurtzeiten_Template_Render.vue.
 *
 * Built BEFORE the variables are substituted, so the record's own note is
 * written into both copies by the same pass that fills everything else in.
 *
 * The note is wrapped rather than emptied into the wrapper: the marked block can
 * be a <p>, and a <div> inside a <p> does not survive being parsed back out of
 * the HTML string this function returns — the parser would close the paragraph
 * and hoist the note out of its own window.
 */
const wrapCommentBlocksForScroll = (root: Element): void => {
  const blocks = Array.from(
    root.querySelectorAll(`[${YURTZEITEN_COMMENT_BLOCK_ATTR}]`)
  );

  blocks.forEach((block) => {
    const parent = block.parentNode;
    const document = block.ownerDocument;
    if (!parent || !document) return;

    // A record's own block is never wrapped. It is copied for the loop, and a
    // second copy of an entry block would take a second record with it —
    // leaving the card drawing a yahrzeit twice and one not at all. A note
    // written straight into the record block scrolls with the block instead.
    if (block.hasAttribute(YURTZEITEN_ENTRY_ATTR)) return;
    if (block.querySelector(`[${YURTZEITEN_ENTRY_ATTR}]`)) return;

    const viewport = document.createElement('div');
    viewport.setAttribute(YURTZEITEN_COMMENT_VIEWPORT_ATTR, 'true');
    const track = document.createElement('div');
    track.setAttribute(YURTZEITEN_COMMENT_TRACK_ATTR, 'true');
    const copy = document.createElement('div');
    copy.setAttribute(YURTZEITEN_COMMENT_COPY_ATTR, 'true');

    // Take the note's place before it is moved, so the wrapper lands exactly
    // where the note stood in the design.
    parent.insertBefore(viewport, block);
    copy.appendChild(block);
    track.appendChild(copy);

    const trailingCopy = copy.cloneNode(true) as Element;
    // The lap that only exists to make the loop seamless. Hidden from assistive
    // technology so the note is announced once, however many times it is drawn.
    trailingCopy.setAttribute('aria-hidden', 'true');
    track.appendChild(trailingCopy);

    viewport.appendChild(track);
  });
};

const isInsideRecordEntryBlock = (node: Text): boolean => {
  const parent = node.parentElement;
  return Boolean(
    parent?.closest(`[${YURTZEITEN_ENTRY_ATTR}="${YURTZEITEN_ENTRY_VALUE}"]`)
  );
};

// The record fields a {token} can name. Kept in step with `substituteVariables`
// above — it replaces `{key}` only when the entry carries that key — so one set
// decides both what gets filled in and what counts as drawing a record.
const RECORD_VARIABLE_KEYS = new Set([
  'englishName', 'firstName', 'lastName',
  'hebrewName', 'gregorianDate', 'hebrewDate', 'comment', 'image',
  // Legacy/editor aliases, as carried on YurtzeitenRenderEntry_N.
  'english', 'hebrew', 'gregorian', 'name',
]);

// Whether a block would show anything of the record handed to it.
//
// An entry block the author emptied — every token deleted, or the whole layout
// lifted out to the card around it — renders identically whichever record it is
// given, so it is not a place a yahrzeit can appear. The husk still sits in the
// saved HTML, which is why the count below has to ask rather than assume.
const entryBlockDrawsRecord = (block: Element): boolean => {
  const walker = block.ownerDocument.createTreeWalker(block, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const value = (walker.currentNode as Text).nodeValue;
    if (!value || value.indexOf('{') === -1) continue;

    const tokens = value.match(/\{(\w+)\}/g) || [];
    if (tokens.some((token) => RECORD_VARIABLE_KEYS.has(token.slice(1, -1)))) {
      return true;
    }
  }

  return false;
};

// The blocks of a design that each draw one record, in document order.
const recordEntryBlocks = (root: Element): Element[] =>
  Array.from(
    root.querySelectorAll(`[${YURTZEITEN_ENTRY_ATTR}="${YURTZEITEN_ENTRY_VALUE}"]`)
  ).filter(entryBlockDrawsRecord);

// How many records ONE card draws.
//
// It is the design's own repeated blocks that decide this, not the template's
// nominal entry count: an author who deletes two of the three blocks has built a
// one-person card, and grouping records three at a time would silently swallow
// two of every three yahrzeits. A design with no blocks left fills its variables
// from the first record, which is a capacity of one all the same.
//
// An emptied block counts the same as a deleted one. A design whose names and
// dates were lifted out to the card, leaving the blocks behind as bare <div>s,
// draws exactly one person however many husks remain — counting them would hand
// the card yahrzeits that appear nowhere on it, and make a board of six look
// full enough to need no second page.
export const countYurtzeitenEntryBlocks_N = (html: string): number => {
  if (!html || !html.trim()) return 0;

  const parsed = new DOMParser().parseFromString(
    `<div id="yz-root">${html}</div>`,
    'text/html'
  );
  const root = parsed.getElementById('yz-root');
  if (!root) return 0;

  return recordEntryBlocks(root).length;
};

export const getYurtzeitenCardCapacity_N = (html: string): number =>
  Math.max(1, countYurtzeitenEntryBlocks_N(html));

// Designs saved before full-width photo rows had an explicit marker still need
// to receive the new layout, but only when the record photo is genuinely alone
// in a root paragraph. A mixed text/image paragraph remains an authored inline
// layout and keeps its pixel width controls.
const markStandaloneYurtzeitenPhotos = (root: Element): boolean => {
  const standalonePhotos = new Set<Element>();
  Array.from(root.children).forEach((child) => {
    if (child.tagName !== 'P') return;

    const photos = Array.from(child.children).filter(
      (element) =>
        element.tagName === 'IMG'
        && element.getAttribute(YURTZEITEN_PHOTO_IMAGE_ATTR) === 'true'
    );
    if (photos.length !== 1) return;

    const photo = photos[0];
    const hasOtherContent = Array.from(child.childNodes).some((node) => {
      if (node === photo) return false;
      if (node.nodeType === 3) return Boolean((node.nodeValue || '').trim());
      return !(node.nodeType === 1 && (node as Element).tagName === 'BR');
    });
    if (!hasOtherContent) standalonePhotos.add(photo);
  });

  let changed = false;
  root
    .querySelectorAll(`img[${YURTZEITEN_PHOTO_IMAGE_ATTR}="true"]`)
    .forEach((photo) => {
      const shouldBeFullWidth = standalonePhotos.has(photo);
      const isFullWidth = photo.getAttribute(YURTZEITEN_PHOTO_FULL_WIDTH_ATTR) === 'true';
      if (shouldBeFullWidth === isFullWidth) return;

      if (shouldBeFullWidth) {
        photo.setAttribute(YURTZEITEN_PHOTO_FULL_WIDTH_ATTR, 'true');
      } else {
        photo.removeAttribute(YURTZEITEN_PHOTO_FULL_WIDTH_ATTR);
      }
      changed = true;
    });
  return changed;
};

// The editor uses the same migration as the renderer, so an older saved design
// is visually identical while being edited and while records are filled in.
export const normalizeYurtzeitenStandalonePhotos_N = (html: string): string => {
  if (!html || !html.trim()) return html || '';

  const parsed = new DOMParser().parseFromString(
    `<div id="yz-root">${html}</div>`,
    'text/html'
  );
  const root = parsed.getElementById('yz-root');
  if (!root) return html;
  return markStandaloneYurtzeitenPhotos(root) ? root.innerHTML : html;
};

// Render an authored design against a list of records.
//
// The design holds the WHOLE layout — one <div data-yz-layout="entry"> per row
// the author laid out — so what the editor shows is what renders. The Nth block
// is filled from the Nth record; blocks past the end of the list keep their
// placeholder text so the layout never collapses.
export const renderYurtzeitenDesignHtml_N = ({
  html,
  entries,
  leadImage = null,
  removeUnfilledEntries = false,
}: RenderYurtzeitenDesignArgs_N): string => {
  if (!html || !html.trim()) return '';

  const parsed = new DOMParser().parseFromString(
    `<div id="yz-root">${html}</div>`,
    'text/html'
  );
  const root = parsed.getElementById('yz-root');
  if (!root) return '';

  markStandaloneYurtzeitenPhotos(root);

  // Before any substitution: once {comment} is replaced there is nothing left to
  // recognise the block by.
  markCommentBlocks(root);
  // And before it too, so the note's second copy is filled in by the same pass
  // as the first rather than needing one of its own.
  wrapCommentBlocksForScroll(root);
  // Before it as well, and for a related reason: a blank line the author left
  // and a field whose {token} the record has no value for both end up as an
  // empty block, and only the first of them is a line the card should draw.
  markAuthoredBlankLines(root);

  // The photo placeholder follows the records only while it is still a stand-in.
  // Once an upload replaces it the attribute is gone and the author's own image
  // is left exactly as they set it.
  const photo = entries.find((item) => item && item.image)?.image || leadImage;
  if (photo) {
    root
      .querySelectorAll(`img[${YURTZEITEN_PHOTO_IMAGE_ATTR}="true"]`)
      .forEach((img) => img.setAttribute('src', photo));
  }

  // Only the blocks that draw a record take one, so the Nth record lands in the
  // Nth block a viewer can actually see it in — the same blocks
  // `countYurtzeitenEntryBlocks_N` sized the card by. Emptied blocks are left
  // exactly as authored: they hold no record, so there is nothing to remove them
  // for either.
  const blocks = recordEntryBlocks(root);

  if (blocks.length) {
    blocks.forEach((block, index) => {
      const entry = entries[index];
      // A block with no record behind it keeps its {tokens} — that is what makes
      // an unfilled row read as a placeholder rather than as blank space.
      if (entry) {
        substituteVariables(block, entry);
      } else if (removeUnfilledEntries) {
        block.remove();
      }
    });
    // Custom designs often add a footer-like field beneath the repeated record
    // blocks. Resolve those card-level variables from the lead record without
    // disturbing the per-record substitutions above.
    if (entries[0]) {
      substituteVariables(
        root,
        entries[0],
        (node) => !isInsideRecordEntryBlock(node)
      );
    }
  } else if (entries[0]) {
    // A design the author stripped of entry blocks still gets its variables
    // filled, from the first record.
    substituteVariables(root, entries[0]);
  }

  return root.innerHTML;
};
